/**
 * Serviço de sincronização completa com paginação
 * Carrega todos os dados da API em lotes para não travar a aplicação
 */

import { apiClient } from './apiClient'
import * as indexedDB from './indexedDBService'
import type { Cliente, Industria, Produto } from '../types'

export interface SyncProgress {
  entity: 'clientes' | 'industrias' | 'produtos' | 'pedidos'
  current: number
  total: number
  percentage: number
  status: 'pending' | 'syncing' | 'completed' | 'error'
  error?: string
}

export interface FullSyncStatus {
  syncing: boolean
  progress: SyncProgress[]
  startTime?: number
  endTime?: number
  totalItems: number
  syncedItems: number
}

type SyncCallback = (status: FullSyncStatus) => void

const BATCH_SIZE = 100 // Quantidade de itens por requisição

/**
 * Sincroniza uma entidade completa em lotes
 */
async function syncEntity<T extends { id: string }>(
  entity: string,
  endpoint: string,
  storeName: string,
  callback?: SyncCallback,
  currentStatus?: FullSyncStatus
): Promise<{ count: number; error?: string }> {
  try {
    let page = 1
    let totalPages = 1
    let allItems: T[] = []
    
    // Primeira requisição para saber o total
    const firstResponse = await apiClient.get<{
      data: T[]
      pagination: { page: number; limit: number; total: number; totalPages: number }
    }>(`${endpoint}?page=1&limit=${BATCH_SIZE}`)

    totalPages = firstResponse.data.pagination.totalPages
    const total = firstResponse.data.pagination.total
    allItems.push(...firstResponse.data.data)

    // Atualiza progresso
    if (callback && currentStatus) {
      const progress = currentStatus.progress.find(p => p.entity === entity)
      if (progress) {
        progress.total = total
        progress.current = allItems.length
        progress.percentage = Math.round((allItems.length / total) * 100)
        progress.status = 'syncing'
      }
      currentStatus.syncedItems += allItems.length
      callback(currentStatus)
    }

    // Busca as páginas restantes
    for (page = 2; page <= totalPages; page++) {
      const response = await apiClient.get<{
        data: T[]
        pagination: { page: number; limit: number; total: number; totalPages: number }
      }>(`${endpoint}?page=${page}&limit=${BATCH_SIZE}`)

      allItems.push(...response.data.data)

      // Atualiza progresso a cada página
      if (callback && currentStatus) {
        const progress = currentStatus.progress.find(p => p.entity === entity)
        if (progress) {
          progress.current = allItems.length
          progress.percentage = Math.round((allItems.length / total) * 100)
        }
        currentStatus.syncedItems += response.data.data.length
        callback(currentStatus)
      }

      // Pequeno delay para não sobrecarregar
      await new Promise(resolve => setTimeout(resolve, 100))
    }

    // Salva tudo no IndexedDB em lotes
    const saveBatchSize = 50
    for (let i = 0; i < allItems.length; i += saveBatchSize) {
      const batch = allItems.slice(i, i + saveBatchSize)
      await indexedDB.putMany(storeName, batch)
    }

    // Marca como sincronizado
    await indexedDB.markSynced(entity)

    // Atualiza progresso final
    if (callback && currentStatus) {
      const progress = currentStatus.progress.find(p => p.entity === entity)
      if (progress) {
        progress.status = 'completed'
        progress.current = allItems.length
        progress.percentage = 100
      }
      callback(currentStatus)
    }

    console.log(`✅ ${allItems.length} ${entity} sincronizados`)
    return { count: allItems.length }
  } catch (error) {
    console.error(`❌ Erro ao sincronizar ${entity}:`, error)
    const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido'
    
    if (callback && currentStatus) {
      const progress = currentStatus.progress.find(p => p.entity === entity)
      if (progress) {
        progress.status = 'error'
        progress.error = errorMessage
      }
      callback(currentStatus)
    }
    
    return { count: 0, error: errorMessage }
  }
}

/**
 * Sincroniza todos os dados em lotes
 */
export async function syncAllDataPaginated(
  callback?: SyncCallback
): Promise<FullSyncStatus> {
  console.log('🔄 Iniciando sincronização completa paginada...')

  const status: FullSyncStatus = {
    syncing: true,
    startTime: Date.now(),
    totalItems: 0,
    syncedItems: 0,
    progress: [
      { entity: 'clientes', current: 0, total: 0, percentage: 0, status: 'pending' },
      { entity: 'industrias', current: 0, total: 0, percentage: 0, status: 'pending' },
      { entity: 'produtos', current: 0, total: 0, percentage: 0, status: 'pending' },
    ],
  }

  // Só sincroniza se estiver online
  if (!navigator.onLine) {
    console.log('📡 Offline - não é possível sincronizar')
    status.syncing = false
    status.progress.forEach(p => {
      p.status = 'error'
      p.error = 'Sem conexão com a internet'
    })
    if (callback) callback(status)
    return status
  }

  try {
    // Calcula total preliminar
    try {
      const [clientesPreview, industriasPreview, produtosPreview] = await Promise.all([
        apiClient.get<{ pagination: { total: number } }>('/clientes?page=1&limit=1'),
        apiClient.get<{ pagination: { total: number } }>('/industrias?page=1&limit=1'),
        apiClient.get<{ pagination: { total: number } }>('/produtos?page=1&limit=1'),
      ])

      status.totalItems = 
        clientesPreview.data.pagination.total +
        industriasPreview.data.pagination.total +
        produtosPreview.data.pagination.total

      status.progress[0].total = clientesPreview.data.pagination.total
      status.progress[1].total = industriasPreview.data.pagination.total
      status.progress[2].total = produtosPreview.data.pagination.total
      
      if (callback) callback(status)
    } catch (error) {
      console.warn('Não foi possível obter totais preliminares:', error)
    }

    // Sincroniza cada entidade sequencialmente
    await syncEntity<Cliente>('clientes', '/clientes', indexedDB.STORES.CLIENTES, callback, status)
    await syncEntity<Industria>('industrias', '/industrias', indexedDB.STORES.INDUSTRIAS, callback, status)
    await syncEntity<Produto>('produtos', '/produtos', indexedDB.STORES.PRODUTOS, callback, status)

    // Finaliza
    status.syncing = false
    status.endTime = Date.now()
    
    console.log(`✅ Sincronização completa concluída em ${((status.endTime - status.startTime!) / 1000).toFixed(2)}s`)
    console.log(`📊 Total sincronizado: ${status.syncedItems} itens`)
    
    if (callback) callback(status)
    return status
  } catch (error) {
    console.error('❌ Erro na sincronização completa:', error)
    status.syncing = false
    status.endTime = Date.now()
    if (callback) callback(status)
    return status
  }
}

/**
 * Obtém dados com paginação (para scroll infinito)
 */
export async function getPaginatedData<T>(
  endpoint: string,
  page: number,
  limit: number = 50,
  search?: string
): Promise<{
  data: T[]
  pagination: { page: number; limit: number; total: number; totalPages: number }
}> {
  const params = new URLSearchParams()
  params.append('page', page.toString())
  params.append('limit', limit.toString())
  if (search) params.append('search', search)

  const response = await apiClient.get<{
    data: T[]
    pagination: { page: number; limit: number; total: number; totalPages: number }
  }>(`${endpoint}?${params}`)

  return response.data
}
